<script>
import axios from "axios";
import QRCode from "qrcode";
import TrendChart from "@/components/referral/TrendChart.vue";

export default {
  name: "AdminStudentReferralShow",
  components: { TrendChart },

  data: () => ({
    id: null,
    // One endpoint carries both the inviter's identity and their stats, so there is no
    // separate "profile" object the way the Qabul xodimi page has one.
    item: null,
    series: [],
    students: [],
    loading: false,
    copied: false,
    qr: "",
    animConv: 0,
  }),

  computed: {
    referralLink() {
      return this.item?.refToken
        ? `https://qabul.usat.uz/?student=${this.item.refToken}`
        : "";
    },
    conversion() {
      return Math.max(0, Math.min(100, Number(this.item?.conversion) || 0));
    },
    dash() {
      const C = 2 * Math.PI * 52;
      return { C, offset: C * (1 - this.animConv / 100) };
    },
    bestDay() {
      if (!this.series.length) return null;
      return this.series.reduce((a, b) =>
        (Number(b.applicants) || 0) > (Number(a.applicants) || 0) ? b : a
      );
    },
  },

  watch: {
    referralLink: "renderQr",
  },

  async mounted() {
    this.id = this.$route.params.id;
    await this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        const [item, series, students] = await Promise.all([
          axios.get(`/admin/student-referral/${this.id}`),
          axios.get(`/admin/student-referral/${this.id}/timeseries`),
          axios.get(`/admin/student-referral/${this.id}/students`),
        ]);
        this.item = item.data || null;
        this.series = Array.isArray(series.data) ? series.data : [];
        this.students = Array.isArray(students.data) ? students.data : [];
        this.renderQr();
        this.animateConversion();
      } catch {
        this.item = null;
        this.series = [];
        this.students = [];
      } finally {
        this.loading = false;
      }
    },

    animateConversion() {
      const target = this.conversion;
      const dur = 850;
      const start = Date.now();
      const from = this.animConv;
      const tick = () => {
        const t = Math.min(1, (Date.now() - start) / dur);
        const e = 1 - Math.pow(1 - t, 3);
        this.animConv = from + (target - from) * e;
        if (t < 1) requestAnimationFrame(tick);
        else this.animConv = target;
      };
      requestAnimationFrame(tick);
    },

    async renderQr() {
      if (!this.referralLink) {
        this.qr = "";
        return;
      }
      try {
        this.qr = await QRCode.toDataURL(this.referralLink, {
          margin: 1,
          width: 320,
          color: { dark: "#0c1322ff", light: "#ffffffff" },
        });
      } catch {
        this.qr = "";
      }
    },

    async copyLink() {
      const link = this.referralLink;
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    },

    fmtPct(v) {
      const n = Number(v) || 0;
      return `${n.toFixed(n % 1 === 0 ? 0 : 1)}%`;
    },
    fmtDate(d) {
      if (!d) return "";
      return new Date(d + "T00:00:00Z").toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    },
    fmtFull(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "—";
      return dt.toLocaleDateString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    fmtSum(n) {
      const v = Number(n);
      if (!v) return "";
      return v.toLocaleString("ru-RU") + " so'm";
    },
    statusMeta(s) {
      const map = {
        DRAFT: { label: "Qoralama", color: "#6b7891" },
        SUBMITTED: { label: "Topshirilgan", color: "#38bdf8" },
        EXAMINED: { label: "Imtihon topshirgan", color: "#38bdf8" },
        ACCEPTED: { label: "Qabul qilingan", color: "#34d399" },
        PASSED: { label: "O'tgan", color: "#34d399" },
        FAILED: { label: "O'tmagan", color: "#f87171" },
        REJECTED: { label: "Rad etilgan", color: "#f87171" },
      };
      return map[s] || { label: s || "—", color: "#6b7891" };
    },
    statusLabel(s) {
      return this.statusMeta(s).label;
    },
    statusStyle(s) {
      const c = this.statusMeta(s).color;
      return { color: c, background: c + "22", borderColor: c + "55" };
    },
    goBack() {
      this.$router.push("/talaba-referallari-2026-2027");
    },
    openApplication(s) {
      // s.id is the application id (ReferredStudentDTO.id) → application detail page
      if (s && s.id != null) this.$router.push(`/application-show/${s.id}`);
    },
  },
};
</script>

<template>
  <v-container fluid class="rmx">
    <!-- BACK -->
    <v-btn
      @click="goBack"
      elevation="0"
      rounded
      large
      class="font-made modern-back-btn mb-6 px-6 py-3 d-flex align-center"
      style="
        border: 1px solid #e0e0e0;
        background: linear-gradient(135deg, #ffffff, #f9f9f9);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
        transition: all 0.25s ease;
      "
    >
      <v-icon
        left
        size="22"
        style="color:#0c0c0c; transition: transform 0.2s;"
        class="back-icon"
      >
        mdi-arrow-left
      </v-icon>
      <span
        class="font-weight-bold text-uppercase"
        style="letter-spacing:1px; font-size:14px; color:#0c0c0c;"
      >
        {{ $t("Back") }}
      </span>
    </v-btn>

    <!-- HERO -->
    <header class="rmx-hero">
      <div class="rmx-hero-id" v-if="item">
        <div class="rmx-hero-meta">
          <div class="rmx-eyebrow">{{ $t('Talaba') }}</div>
          <h1 class="rmx-hero-name">{{ item.fullName }}</h1>
          <div class="rmx-hero-contact" v-if="item.phone">
            <span>{{ item.phone }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- TOP GRID: share + donut -->
    <div class="rmx-grid-top">
      <section class="rmx-card rmx-share">
        <div class="rmx-card-eyebrow">{{ $t('Referral link') }}</div>
        <div class="rmx-share-body">
          <div class="rmx-qr">
            <img v-if="qr" :src="qr" :alt="$t('Referral link')" />
          </div>
          <div class="rmx-share-info">
            <code class="rmx-share-link">{{ referralLink }}</code>
            <button class="rmx-share-copy" @click="copyLink">
              <v-icon left size="16">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              {{ copied ? $t('Copied') : $t('Copy link') }}
            </button>
            <p class="rmx-share-hint">
              {{ $t('Taklif qilganlari') }}: {{ item ? item.applicants : 0 }}
            </p>
          </div>
        </div>
      </section>

      <section class="rmx-card rmx-donut-card">
        <div class="rmx-card-eyebrow">{{ $t('Conversion') }}</div>
        <div class="rmx-donut">
          <svg viewBox="0 0 120 120">
            <defs>
              <linearGradient id="rmxRing" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="var(--rmx-gold)" />
                <stop offset="100%" stop-color="var(--rmx-emerald)" />
              </linearGradient>
            </defs>
            <circle class="rmx-ring-track" cx="60" cy="60" r="52" />
            <circle
              class="rmx-ring-val"
              cx="60"
              cy="60"
              r="52"
              :stroke-dasharray="dash.C"
              :stroke-dashoffset="dash.offset"
              transform="rotate(-90 60 60)"
            />
          </svg>
          <div class="rmx-donut-center">
            <span class="rmx-donut-pct">{{ fmtPct(animConv) }}</span>
            <span class="rmx-donut-sub">{{ item ? item.reachedContract : 0 }} / {{ item ? item.applicants : 0 }}</span>
          </div>
        </div>
      </section>
    </div>

    <!-- KPI CARDS -->
    <div class="rmx-grid-kpi">
      <div class="rmx-card rmx-stat rmx-stat--applicants">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Applicants') }}</span>
          <v-icon size="18">mdi-account-multiple</v-icon>
        </div>
        <div class="rmx-stat-num">{{ item ? item.applicants : 0 }}</div>
      </div>
      <div class="rmx-card rmx-stat rmx-stat--applications">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Applications') }}</span>
          <v-icon size="18">mdi-file-document-outline</v-icon>
        </div>
        <div class="rmx-stat-num">{{ item ? item.applications : 0 }}</div>
      </div>
      <div class="rmx-card rmx-stat rmx-stat--contracts">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Contracts') }}</span>
          <v-icon size="18">mdi-handshake-outline</v-icon>
        </div>
        <div class="rmx-stat-num">{{ item ? item.reachedContract : 0 }}</div>
      </div>
      <div class="rmx-card rmx-stat rmx-stat--best">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Best day') }}</span>
          <v-icon size="18">mdi-star-outline</v-icon>
        </div>
        <div class="rmx-stat-num">{{ bestDay ? bestDay.applicants : 0 }}</div>
        <div class="rmx-stat-foot">{{ bestDay ? fmtDate(bestDay.date) : '—' }}</div>
      </div>
    </div>

    <!-- TREND -->
    <section class="rmx-card rmx-trend">
      <div class="rmx-trend-head">
        <div class="rmx-card-eyebrow">{{ $t('Acquisition trend') }}</div>
      </div>
      <trend-chart :points="series" :height="260" />
    </section>

    <!-- REFERRED STUDENTS -->
    <section class="rmx-card rmx-students">
      <div class="rmx-students-head">
        <div class="rmx-card-eyebrow">{{ $t('Referred students') }}</div>
        <span class="rmx-students-count">{{ students.length }}</span>
      </div>

      <div v-if="loading" class="rmx-students-empty">{{ $t('Loading...') }}</div>
      <div v-else-if="!students.length" class="rmx-students-empty">
        {{ $t('Hozircha hech kim taklif qilmagan') }}
      </div>

      <div v-else class="rmx-tablewrap">
        <table class="rmx-table">
          <thead>
            <tr>
              <th class="rmx-col-num">#</th>
              <th>{{ $t('Full name') }}</th>
              <th>{{ $t('Phone') }}</th>
              <th>{{ $t('Registered') }}</th>
              <th>{{ $t('Status') }}</th>
              <th>{{ $t('Direction') }}</th>
              <th>{{ $t('Contract') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(s, i) in students"
              :key="s.id"
              class="rmx-row-link"
              tabindex="0"
              :title="$t('Open application')"
              @click="openApplication(s)"
              @keydown.enter="openApplication(s)"
            >
              <td class="rmx-col-num">{{ i + 1 }}</td>
              <td class="rmx-col-name">{{ s.fullName || '—' }}</td>
              <td>{{ s.phone || '—' }}</td>
              <td>{{ fmtFull(s.registeredDate) }}</td>
              <td>
                <span class="rmx-badge" :style="statusStyle(s.status)">{{ statusLabel(s.status) }}</span>
              </td>
              <td class="rmx-col-faculty">{{ s.faculty || '—' }}</td>
              <td>
                <template v-if="s.contractNumber">
                  <span class="rmx-contract-no">{{ s.contractNumber }}</span>
                  <span v-if="s.contractSum" class="rmx-contract-sum">{{ fmtSum(s.contractSum) }}</span>
                </template>
                <span v-else class="rmx-dim">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </v-container>
</template>

<style lang="scss" scoped>
.rmx {
  --rmx-gold: #ffce4a;
  --rmx-gold-deep: #f4a915;
  --rmx-emerald: #34d399;
  --rmx-blue: #38bdf8;
  --rmx-panel: #ffffff;
  --rmx-ink: #0c1322;
  --rmx-text: #0f1a2e;
  --rmx-muted: #6b7891;
  --rmx-line: rgba(15, 26, 46, 0.09);
  --rmx-line-strong: rgba(15, 26, 46, 0.22);
  --rmx-tip-bg: #ffffff;
  --display: "Saira SemiCondensed", -apple-system, BlinkMacSystemFont, sans-serif;
  padding-bottom: 40px;
}

/* HERO */
.rmx-hero {
  position: relative;
  border-radius: 16px;
  padding: 22px 26px;
  background: linear-gradient(150deg, var(--rmx-ink) 0%, #14203a 60%, #0c1322 100%);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  margin-bottom: 16px;
}
/* ORQAGA pill — full copy from the contract_2026_2027-show page */
.modern-back-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
.modern-back-btn:active {
  transform: translateY(0);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}
.back-icon:hover {
  transform: translateX(-3px);
}
.rmx-hero-id {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
}
.rmx-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.2px;
  text-transform: uppercase;
  color: var(--rmx-gold);
  opacity: 0.85;
}
.rmx-hero-name {
  font-family: var(--display);
  font-weight: 900;
  font-size: 34px;
  line-height: 1.05;
  color: #fff;
  margin: 3px 0 8px;
}
.rmx-hero-contact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: -2px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.72);
}

/* CARDS */
.rmx-card {
  background: var(--rmx-panel);
  border: 1px solid var(--rmx-line);
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 2px rgba(15, 26, 46, 0.05);
}
.rmx-card-eyebrow {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--rmx-muted);
  margin-bottom: 14px;
}

.rmx-grid-top {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

/* SHARE */
.rmx-share-body {
  display: flex;
  gap: 18px;
  align-items: center;
}
.rmx-qr {
  flex-shrink: 0;
  width: 132px;
  height: 132px;
  border-radius: 12px;
  background: #fff;
  padding: 10px;
  border: 1px solid var(--rmx-line);
}
.rmx-qr img {
  width: 100%;
  height: 100%;
  display: block;
}
.rmx-share-info {
  min-width: 0;
  flex: 1;
}
.rmx-share-link {
  display: block;
  font-size: 13px;
  color: var(--rmx-text);
  background: rgba(15, 26, 46, 0.05);
  border-radius: 9px;
  padding: 9px 12px;
  word-break: break-all;
  margin-bottom: 10px;
}
.rmx-share-copy {
  display: inline-flex;
  align-items: center;
  background: var(--rmx-ink);
  color: var(--rmx-gold);
  border: none;
  border-radius: 10px;
  padding: 9px 15px;
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  transition: transform 0.15s ease;
}
.rmx-share-copy:hover {
  transform: translateY(-1px);
}
.rmx-share-copy .v-icon {
  color: var(--rmx-gold) !important;
}
.rmx-share-hint {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--rmx-muted);
}

/* DONUT */
.rmx-donut-card {
  display: flex;
  flex-direction: column;
}
.rmx-donut {
  position: relative;
  flex: 1;
  display: grid;
  place-items: center;
  min-height: 150px;
}
.rmx-donut svg {
  width: 150px;
  height: 150px;
}
.rmx-ring-track {
  fill: none;
  stroke: rgba(15, 26, 46, 0.08);
  stroke-width: 11;
}
.rmx-ring-val {
  fill: none;
  stroke: url(#rmxRing);
  stroke-width: 11;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.2s linear;
}
.rmx-donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.rmx-donut-pct {
  font-family: var(--display);
  font-weight: 900;
  font-size: 32px;
  color: var(--rmx-text);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.rmx-donut-sub {
  font-size: 12px;
  font-weight: 700;
  color: var(--rmx-muted);
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
}

/* KPI */
.rmx-grid-kpi {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.rmx-stat {
  position: relative;
  overflow: hidden;
}
.rmx-stat::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}
.rmx-stat--applicants::before { background: var(--rmx-gold); }
.rmx-stat--applications::before { background: var(--rmx-blue); }
.rmx-stat--contracts::before { background: var(--rmx-emerald); }
.rmx-stat--best::before { background: #c084fc; }
.rmx-stat-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.rmx-stat-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--rmx-muted);
}
.rmx-stat-top .v-icon {
  color: var(--rmx-muted) !important;
  opacity: 0.6;
}
.rmx-stat-num {
  font-family: var(--display);
  font-weight: 900;
  font-size: 38px;
  line-height: 1.1;
  color: var(--rmx-text);
  font-variant-numeric: tabular-nums;
  margin-top: 8px;
}
.rmx-stat-foot {
  font-size: 12px;
  font-weight: 700;
  color: var(--rmx-muted);
}

/* TREND */
.rmx-trend {
  padding-bottom: 14px;
}

/* REFERRED STUDENTS */
.rmx-students {
  margin-top: 16px;
}
.rmx-students-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.rmx-students-count {
  font-family: var(--display);
  font-weight: 800;
  font-size: 13px;
  color: var(--rmx-muted);
  background: rgba(15, 26, 46, 0.05);
  border-radius: 999px;
  padding: 2px 10px;
}
.rmx-students-empty {
  padding: 26px 4px 6px;
  text-align: center;
  color: var(--rmx-muted);
  font-weight: 600;
  font-size: 13px;
}
.rmx-tablewrap {
  overflow-x: auto;
  margin-top: 6px;
}
.rmx-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}
.rmx-table thead th {
  text-align: left;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--rmx-muted);
  padding: 0 12px 10px;
  border-bottom: 1px solid var(--rmx-line);
  white-space: nowrap;
}
.rmx-table tbody td {
  padding: 12px;
  border-bottom: 1px solid var(--rmx-line);
  color: var(--rmx-text);
  vertical-align: middle;
  transition: background 0.12s ease;
}
.rmx-table tbody tr:last-child td {
  border-bottom: none;
}
.rmx-row-link {
  cursor: pointer;
}
.rmx-row-link:focus-visible {
  outline: 2px solid var(--rmx-gold);
  outline-offset: -2px;
}
.rmx-table tbody tr:hover td {
  background: rgba(255, 206, 74, 0.1);
}
.rmx-col-num {
  width: 44px;
  color: var(--rmx-muted);
  font-variant-numeric: tabular-nums;
}
.rmx-col-name {
  font-weight: 700;
  white-space: nowrap;
}
.rmx-col-faculty {
  max-width: 240px;
}
.rmx-badge {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
}
.rmx-contract-no {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.rmx-contract-sum {
  display: block;
  font-size: 11.5px;
  color: var(--rmx-muted);
  margin-top: 1px;
}
.rmx-dim {
  color: var(--rmx-muted);
}

@media (max-width: 1080px) {
  .rmx-grid-top {
    grid-template-columns: 1fr;
  }
  .rmx-grid-kpi {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 620px) {
  .rmx-share-body {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* DARK */
.theme--dark .rmx {
  --rmx-panel: #161922;
  --rmx-ink: #090c14;
  --rmx-text: #eef1f7;
  --rmx-muted: #8b94a8;
  --rmx-line: rgba(255, 255, 255, 0.08);
  --rmx-line-strong: rgba(255, 255, 255, 0.2);
  --rmx-tip-bg: #1b1f2a;
}
.theme--dark .rmx-share-link,
.theme--dark .rmx-ring-track {
  background: rgba(255, 255, 255, 0.07);
}
.theme--dark .rmx-ring-track {
  stroke: rgba(255, 255, 255, 0.08);
}
.theme--dark .rmx-students-count {
  background: rgba(255, 255, 255, 0.07);
}
.theme--dark .rmx-table tbody tr:hover td {
  background: rgba(255, 206, 74, 0.07);
}
</style>
